/* Phone number normalization for SMS Higlo notifications.

   We currently support US (+1) and UK (+44) numbers — the SMS channel is a
   US toll-free sender (US/Canada) plus a "Higlo" alphanumeric sender (UK).
   Everything is stored E.164. Canada shares +1 and works on the same
   channel, so +1 numbers are accepted as "US" without distinguishing.

   UK numbers must be mobile (07… / +447…) — SMS to UK landlines isn't a
   thing our recipients would want, and carriers reject most of it. */

export const SUPPORTED_COUNTRIES = [
  { code: 'US', label: 'United States', dial: '+1', placeholder: '(555) 123-4567' },
  { code: 'UK', label: 'United Kingdom', dial: '+44', placeholder: '07123 456789' },
];

/** Strip formatting to bare digits. */
function digitsOf(input) {
  return String(input || '').trim().replace(/\D/g, '');
}

/**
 * Normalize a user-typed phone number to E.164 for the selected country.
 *
 * @param {string} input  raw user input
 * @param {'US'|'UK'} country
 * @returns {{ e164: string|null, error: string|null }}
 */
export function normalizePhone(input, country) {
  const digits = digitsOf(input);
  if (!digits) {
    return { e164: null, error: 'Enter a phone number.' };
  }

  if (country === 'US') {
    // Accept: 10 national digits, or 11 starting with the +1 country code.
    let national = digits;
    if (national.length === 11 && national.startsWith('1')) {
      national = national.slice(1);
    }
    if (national.length !== 10) {
      return { e164: null, error: 'US numbers have 10 digits, e.g. (555) 123-4567.' };
    }
    if (national[0] === '0' || national[0] === '1') {
      return { e164: null, error: "That doesn't look like a valid US number." };
    }
    return { e164: `+1${national}`, error: null };
  }

  if (country === 'UK') {
    // Accept the common written forms: 07XXXXXXXXX, +447XXXXXXXXX, 447XXXXXXXXX,
    // "+44 (0)7…" (keeps the 0 after the country code), and "0044 7…".
    let national = digits;
    // Strip a leading 00 international prefix (0044… → 44…).
    if (national.startsWith('00')) national = national.slice(2);
    // Convert a +44 / 44 country code to the national 0-form.
    if (national.startsWith('44')) national = `0${national.slice(2)}`;
    // Collapse the redundant leading 0 from the "+44 (0)7…" form (00… → 0…).
    if (national.startsWith('00')) national = national.slice(1);
    if (!(national.length === 11 && national.startsWith('07'))) {
      return {
        e164: null,
        error: 'UK numbers must be a mobile starting 07, e.g. 07123 456789.',
      };
    }
    // Reject non-mobile 07 ranges: 070 (personal numbering) and 076 pagers
    // (except 07624, Isle of Man mobile).
    if (national.startsWith('070')
        || (national.startsWith('076') && !national.startsWith('07624'))) {
      return {
        e164: null,
        error: 'That looks like a non-mobile number — enter a UK mobile.',
      };
    }
    return { e164: `+44${national.slice(1)}`, error: null };
  }

  return { e164: null, error: 'Unsupported country.' };
}

/** Display form for a stored E.164 number: +1 (555) 123-4567 / +44 7123 456789 */
export function formatPhoneForDisplay(e164) {
  if (!e164 || typeof e164 !== 'string') return '';
  if (e164.startsWith('+1') && e164.length === 12) {
    return `+1 (${e164.slice(2, 5)}) ${e164.slice(5, 8)}-${e164.slice(8)}`;
  }
  if (e164.startsWith('+44') && e164.length === 13) {
    return `+44 ${e164.slice(3, 7)} ${e164.slice(7)}`;
  }
  return e164;
}
